import _ from 'underscore';
import Backbone from 'backbone';
import {
  BaseView,
  type BaseViewConstructor,
  type BaseViewInstance,
  type BaseViewOptions,
} from '@bingads-webui/base-view';
import { RenderEngine } from './render-engine';

export type DeterministicViewState = object;

export interface DeterministicViewOptions extends BaseViewOptions {
  renderInSync?: boolean;
}

export type DeterministicModel<TState extends object = DeterministicViewState> =
  Omit<Backbone.Model, 'attributes' | 'toJSON'> & {
  attributes: TState;
  toJSON(): TState;
};

export type ViewEventHandler = (...args: unknown[]) => void;

export type RenderTemplate<TState extends object = DeterministicViewState> = (options: {
  model: TState;
  view: DeterministicViewInstance<TState>;
}) => string;

export type RenderEngineLike = Pick<
  RenderEngine,
  'renderView' | 'scheduleRenderView'
>;

export interface DeterministicViewInstance<
  TState extends object = DeterministicViewState,
> extends BaseViewInstance {
  model: DeterministicModel<TState>;
  options: DeterministicViewOptions;
  renderEngine: RenderEngineLike;
  template: RenderTemplate<TState>;
  Model: new () => DeterministicModel<TState>;
  initialize(options?: never): void;
  setState(state: Partial<TState>): this;
  getState(): TState;
  renderTrunk(): void;
  render(): this;
  listenTo(object: object, events: string, callback: ViewEventHandler): this;
  once(events: string, callback: ViewEventHandler, context?: object): this;
}

export type TypedDeterministicView<TState extends object> =
  DeterministicViewInstance<TState>;

type InheritedBaseViewStatics = Pick<
  BaseViewConstructor,
  | 'eventEmitter'
  | 'isValid'
  | 'getChildrenRefs'
  | 'getChildren'
  | 'getParent'
  | 'getChild'
  | 'getViewForElement'
>;

export interface DeterministicViewConstructor extends InheritedBaseViewStatics {
  new (options?: DeterministicViewOptions): DeterministicViewInstance;
  extend(properties: object, classProperties?: object): DeterministicViewConstructor;
  defaultRenderEngine: RenderEngine;
  prototype: DeterministicViewInstance;
}

const defaultRenderEngine = new RenderEngine();

/**
 * Your template should only render the trunk of the view. For the child views,
 * you should put a placeholder with attribute `data-ref` being the ref of the
 * child view.
 * @callback TemplateCallback
 * @param {Object} options -
 * @param {Object} options.model - The model object
 * @param {DeterministicView} options.view - The view being rendered
 * @returns {string} - The rendered HTML
 */
const EMPTY_TEMPLATE = () => '';

const FORBIDDEN_OPTIONS = [
  'model',
  'collection',
  'el',
  'id',
  'className',
  'tagName',
  'attributes',
  'events',
];

export const DeterministicView: DeterministicViewConstructor = (BaseView as BaseViewConstructor & {
  extend(properties: object, classProperties?: object): DeterministicViewConstructor;
}).extend({
  // We cannot use ES6 `extends` here, because we need to use `this` before
  // calling `super(..args)`.
  // This is due to the limitation of `Backbone.View`. The constructor of
  // `Backbone.View` calls the `initialize` method, which is commonly used to
  // initialize the view instance.
  // We want give user the chance to use their own Model type instead of
  // `Backbone.Model`, that requires us to access `this.Model` to create the
  // instance of model, and pass it to `initialize()`

  /**
   * An extension of BaseView
   * @constructor DeterministicView
   * @param {object} options -
   *    The options to create view. NOTE, the builtin options `model`,
   *    `collection`, `el`, `id`, `className`, `tagName`, `attributes` and
   *    `events` are forbidden for this constructor. Those can be customized
   *    at class level. But for instances of the same view class, all those
   *    properties MUST be consistent.
   * @return {void}
   */
  constructor: function constructor(
    this: DeterministicViewInstance,
    options?: DeterministicViewOptions
  ) {
    _.each(FORBIDDEN_OPTIONS, (name: string) => {
      if (_.has(options as object, name)) {
        throw new Error(`Option '${name}' is forbidden for DeterministicView`);
      }
    });

    const ModelConstructor = this.Model;
    const model = new ModelConstructor();

    if (!(model instanceof Backbone.Model)) {
      throw new Error('Invalid Model type');
    }

    BaseView.call(this, _.defaults({ model }, options));

    // This is due to we are using Backbone 1.3.3 in AdsAppUISharedComponents.
    // In AdsAppUI, we are using 1.0.0
    // We can remove following logic about options if we downgrade all to 1.0.0
    if (!this.options) {
      this.options = {};
    }
    _.defaults(this.options, options);

    // Redraw automatically on model change
    this.listenTo(model, 'change', () => {
      this.renderEngine.scheduleRenderView(this);
    });
    this.renderEngine.scheduleRenderView(this);
  },

  /**
   * The RenderEngine to render this view. This can be overridden by the
   * subclasses to change the rendering behavior (e.g. delayed rendering).
   * @memberof DeterministicView#
   * @type {RenderEngine}
   */
  get renderEngine() {
    return defaultRenderEngine;
  },

  /**
   * The template to render the view's trunk. This should be overridden by the
   * subclasses to fill the content. By default, it's an empty template.
   * @memberof DeterministicView#
   * @type {TemplateCallback}
   */
  get template() {
    return EMPTY_TEMPLATE;
  },

  /**
   * The class of the model. This can be overridden by subclasses to use
   * customized model type. By default, it's `Backbone.Model`.
   * @memberof DeterministicView#
   * @type {Class}
   */
  get Model() {
    return Backbone.Model;
  },

  /**
   * Set state of the view. This should be overridden by subclasses to propagate
   * state to their children views. By default, it stores the whole state into
   * the view's model.
   * @memberof DeterministicView#
   * @param {Object} state - The new state
   * @returns {DeterministicView} - The view itself
   */
  setState(this: DeterministicViewInstance, state: object) {
    this.model.set(state);
    return this;
  },

  /**
   * Get state of the view. This should be overridden by subclasses to collect
   * state from their children views. By default, it return the view's model as
   * a JSON.
   * @memberof DeterministicView#
   * @returns {Object} - The current state of the view
   */
  getState(this: DeterministicViewInstance) {
    return this.model.toJSON();
  },

  /**
   * Render the trunk of the view. This can be overridden by subclasses to add
   * customized behaviors before/after the DOM elements are generated, without
   * concerning about the children views. By default, it fills the `$el` with
   * the HTML generated from template
   * @memberof DeterministicView#
   * @returns {void}
   */
  renderTrunk(this: DeterministicViewInstance) {
    this.$el.html(this.template({
      model: this.model.attributes,
      view: this,
    }));
  },

  /**
   * Render the view immediately.
   * @memberof DeterministicView#
   * @returns {DeterministicView} - The view itself
   */
  render(this: DeterministicViewInstance) {
    this.renderEngine.renderView(this);
    return this;
  },
}, {
  /**
   * The default render engine of `DeterministicView`
   * @memberof DeterministicView
   * @constant
   */
  defaultRenderEngine,
});
